import { Formik } from 'formik';
import { useMemo } from 'react';
import { object, ObjectSchema, string } from 'yup';

import { InsuranceMetaGrouping } from '@uplift/api';
import { useIsMobile } from '@uplift/hooks';
import { useInsuranceCompaniesQuery } from '@uplift/queries';
import { Autocomplete, Button } from '@uplift/ui-v2';

import { FullScreenSelect } from '../../../components/full-screen-select';
import {
  StepContainer, StepContent, StepDescription, StepForm, StepHeading, StepNavigator, StepNextButton
} from '../../components';
import { SELF_PAY_ID } from '../../utils';
import { InsuranceNextData } from '../types';
import groupBy from 'lodash/groupBy';

export type InsuranceProps = {
  onNext: (data: InsuranceNextData) => void;
  onBack: () => void;
  insuranceId: string | null;
  stateId: string;
  title?: string;
  isBetterhelpUser?: boolean;
};

type InsuranceFormValues = {
  insuranceId: string;
};

export const Insurance: React.VFC<InsuranceProps> = props => {
  const isMobile = useIsMobile();

  const insuranceId = useMemo(() => {
    if (props.insuranceId === SELF_PAY_ID) {
      return null;
    }

    return props.insuranceId;
  }, [ props.insuranceId ]);

  const title = useMemo(() => {
    if (props.isBetterhelpUser) {
      return "Let's find your psychiatrist";
    }

    return props.title ?? 'Do you have insurance you can use?';
  }, [ props.isBetterhelpUser, props.title ]);
  const description = useMemo(() => {
    if (props.isBetterhelpUser) {
      return 'BetterHelp shared some of your details, now we need your insurance to match you with the right provider.';
    }

    return null;
  }, [ props.isBetterhelpUser ]);

  return (
    <Formik
      initialValues={{ insuranceId: insuranceId }}
      onSubmit={props.onNext}
      validationSchema={validationSchema}
      validateOnMount
    >
      {formik => (
        <StepContainer>
          <StepContent>
            <StepHeading>{title}</StepHeading>
            {description && (
              <StepDescription>
                {description}
              </StepDescription>
            )}
            <StepForm onSubmit={formik.handleSubmit} maxWidth="384px">
              {isMobile ? <MobileInsuranceSelectInput stateId={props.stateId} /> : <DesktopInsuranceSelectInput stateId={props.stateId} />}
            </StepForm>
          </StepContent>
          <StepNavigator onBack={props.onBack}>
            <StepNextButton disabled={!formik.isValid} />
            <Button color="secondary" onClick={() => props.onNext({ insuranceId: SELF_PAY_ID })} data-testid="pay-myself-btn">I’ll pay for services myself</Button>
          </StepNavigator>
        </StepContainer>
      )}
    </Formik>
  );
};

const validationSchema = (): ObjectSchema<InsuranceFormValues> => object({
  insuranceId: string().nullable().required('Please select an insurance').defined()
});

type InsuranceSelectProps = {
  stateId: string;
};

const DesktopInsuranceSelectInput: React.VFC<InsuranceSelectProps> = props => {
  const { data: companies = [] } = useInsuranceCompaniesQuery({ stateId: props.stateId });
  const groupedInsurances = groupBy(companies, company => company.metaGrouping ?? 'partner');

  Object.keys(groupedInsurances).forEach(key => {
    groupedInsurances[key] = [ ...groupedInsurances[key] ].sort((a, b) => {
      if (key === InsuranceMetaGrouping.medicare) {
        if (a.name === 'Medicare (Original)') return -1;
        if (b.name === 'Medicare (Original)') return 1;
      }
      return a.name.localeCompare(b.name);
    });
  });

  const orderedInsurances = [
    ...(groupedInsurances['partner'] || []),
    ...(groupedInsurances[InsuranceMetaGrouping.medicare] || []),
    ...(groupedInsurances[InsuranceMetaGrouping.medicaid] || []),
  ];

  return (
    <Autocomplete
      disableClearable
      fullWidth
      name="insuranceId"
      label="Your insurance"
      options={orderedInsurances}
      transformValue={v => v?.id}
      getOptionLabel={opt => opt?.name ?? ''}
      getOptionDisabled={o => !o?.id}
      groupBy={(option) => {
        switch (option.metaGrouping) {
          case InsuranceMetaGrouping.medicaid:
            return 'Medicaid';
          case InsuranceMetaGrouping.medicare:
            return 'Medicare';
          default:
            return 'Uplift insurance partners';
        }
      }}
    />
  );
};

const MobileInsuranceSelectInput: React.VFC<InsuranceSelectProps> = props => {
  const { data: _companies = [] } = useInsuranceCompaniesQuery({ stateId: props.stateId });
  const companies = useMemo(() => {
    return [ ..._companies ].sort((a, b) => a.name.localeCompare(b.name));
  }, [ _companies ]);

  return (
    <FullScreenSelect
      options={companies}
      getLabel={(option) => option?.name || ''}
      getValue={(option) => option.id}
      name="insuranceId"
      label="Your insurance"
      groupBy={(option) => {
        switch (option.metaGrouping) {
          case InsuranceMetaGrouping.medicaid:
            return 'Medicaid';
          case InsuranceMetaGrouping.medicare:
            return 'Medicare';
          default:
            return 'Uplift insurance partners';
        }
      }}
      groupSortOrder={[ 'Uplift insurance partners', 'Medicare', 'Medicaid' ]}
    />
  );
};
